import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCardIcon from '@mui/icons-material/AddCard';
import { format } from 'date-fns';
import { fileAccessService } from 'app/services/fileAccess.service';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { stringToColor } from 'util/helpers';
import { GroupCardType } from './GroupsCardDisplay/GroupCard';
import { useTranslation } from 'react-i18next';
import { GroupProjectType } from './GroupsCardDisplay/ProjectCard';
import { captureException } from '@sentry/react';
import { useCardUtilContext } from 'app/context/CardGroupUtilContext';
import { EditGroupFormValues } from './EditGroupForm';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectSearchAndFilter } from 'app/store/filtering/searchAndFilterSlice';
import { generateProjectGroupFilterObject } from 'app/store/filtering/projectGroupFilterConfig';
import { generateCardGroupFilterObject } from 'app/store/filtering/cardGroupFilterConfig';
import { floatToCurrencyValue } from 'app/shared-components/util/Currency';

import { useUpdateFirstRunState } from 'app/hooks/useUpdateFirstRunState';
import { MoreVertMenuOptionProps } from 'app/shared-components/MoreVertMenu/MoreVertMenu';
import { EditOutlined as EditIcon } from '@mui/icons-material';
import { ResponsiveTableHeadColumn } from 'app/shared-components/layout/table/ResponsiveTableHead';
import { SidesheetRoutes, useSidesheet } from 'app/shared-components/sidesheets/SidesheetProvider';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useCallback, useMemo, useState } from 'react';
import {
  fetchGroupCards,
  loadMoreGroupCards,
  updateCardGroup,
} from 'app/pages/store/CardDataSlice/cardsThunks';
import {
  resetGroupCards,
  resetAssinableCards,
  resetCardSlice,
} from 'app/pages/store/CardDataSlice/cardDataSlice';
import { getMonthlySpendFromMonthlyBudgets } from 'app/shared-components/util/utils';
import { addCardsToGroup, getCardGroup, getProjectGroupData } from 'app/services/groupService';
import {
  getGroups,
  newGroup,
  removeGroup,
  updateGroup,
  selectCardGroupsNextToken,
  updateGroupAdmins,
  resetGroupsSlice,
  resetSelectedGroupId,
  selectSelectedGroup,
  setSelectedGroupId,
} from 'app/pages/store/groupsSlice';
import { setGroupAdmins } from 'app/pages/store/usersSlice';
import { CardGroup, PaidolUser, ProjectGroup } from 'API';
import {
  getProjectGroups,
  newProjectGroup,
  removeProjectGroup,
  updateProject,
  resetProjectGroupsSlice,
  selectSelectedProjectGroup,
  setSelectedProjectGroupId,
  updateProjectGroupAdmins,
  resetSelectedProjectGroupId,
  selectProjectGroupsNextToken,
  updateProjectGroupInState,
} from 'app/pages/store/projectGroupSlice';

export enum GroupTypes {
  CardGroups = 'Card Groups',
  ProjectGroups = 'Project Groups',
}

export const sortByName = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

/**
 * SEE-1352: `paidolUsers` is ordered by paidol_id (the byUserId GSI range key),
 * so index 0 is whichever company sorts first — not the one the user is looking
 * at. Managed groups are per-membership, so the caller must say which company
 * it means. Prefer `selectCurrentPaidolUser` when reading straight from Redux.
 *
 * `selectedPaidolId` is deliberately required, not optional: omitting it would
 * match no membership and silently return [] — a Group Admin with no managed
 * groups at all — with no compile error, and the `no-restricted-syntax` rule
 * only catches the `[0]` form so it would not flag that either.
 */
export const getManagedGroupsFromUser = (
  paidolUsers: PaidolUser[] = [],
  selectedPaidolId: string | null
): CardGroup[] => {
  const paidolUser = paidolUsers.find(
    (pu) => pu.paidol?.id === selectedPaidolId || pu.paidol_id === selectedPaidolId
  );
  const managedGroups = paidolUser?.managedGroups?.items ?? [];

  return managedGroups.map((item) => item?.cardGroup).filter((group): group is CardGroup => Boolean(group));
};

export const mapToGroupCard = (
  cardGroups: CardGroup[] | undefined,
  paidolUserPictureMap: Record<string, string>
): GroupCardType[] => {
  if (!cardGroups) return [];

  const mappedGroups = cardGroups.map((group) => {
    const monthlyBudget = group.monthlyBudget?.value ?? 0;
    let monthlySpend = getMonthlySpendFromMonthlyBudgets(group.monthlyBudgetSpend?.items);

    if ('spentThisMonth' in group) {
      monthlySpend = group.spentThisMonth?.amount ?? monthlySpend;
    }

    const paidolUsers =
      group.paymentCards?.items?.flatMap(
        (card) => card?.paidolUsers?.items?.flatMap((rel) => (rel?.paidolUser ? [rel.paidolUser] : [])) ?? []
      ) ?? [];

    const distinctUsers = paidolUsers.reduce<Record<string, PaidolUser>>((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});

    const users = Object.entries(distinctUsers).map(([id, user]) => {
      const firstName = user.user?.first_name ?? '';
      const lastName = user.user?.last_name ?? '';
      const name = `${firstName} ${lastName}`.trim();
      const email = user.email || user.user?.email || '';

      return {
        paidolUserId: id,
        email,
        name,
        picture: paidolUserPictureMap[id],
      };
    });

    const groupAdmins =
      group.groupAdmins?.items
        ?.map((admin) => admin?.paidolUser)
        .filter((admin): admin is PaidolUser => Boolean(admin)) ?? [];

    return {
      yearAndMonth: format(new Date(), 'yyyy-MM'),
      id: group.id,
      name: group.name,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt,
      cards: group.paymentCards?.items?.length ?? 0,
      users,
      isMonthlyBudgetUnlimited: group.isMonthlyBudgetUnlimited || false,
      spent: { currency: 'USD', value: monthlySpend },
      budget: group.isMonthlyBudgetUnlimited ? undefined : { currency: 'USD', value: monthlyBudget },
      groupAdmins,
    };
  });

  // Don't apply sortByName here - preserve the sort order from the backend
  // Sorting is handled by applyCardGroupsSortFilter thunk
  return mappedGroups;
};

export const mapToProjectGroup = (projectGroups: ProjectGroup[] | undefined): GroupProjectType[] => {
  if (!projectGroups) return [];

  const mappedGroups = projectGroups.map((group) => {
    const monthlyBudget = group.monthlyBudget?.value ?? 0;
    let monthlySpend = getMonthlySpendFromMonthlyBudgets(group.monthlyBudgetSpend?.items ?? []);

    if ('spentThisMonth' in group) {
      monthlySpend =
        (group as { spentThisMonth?: { amount?: number } }).spentThisMonth?.amount ?? monthlySpend;
    }

    const groupAdmins =
      group.groupAdmins?.items
        ?.map((admin) => admin?.paidolUser)
        .filter((admin): admin is PaidolUser => Boolean(admin)) ?? [];

    return {
      yearAndMonth: format(new Date(), 'yyyy-MM'),
      id: group.id,
      name: group.name,
      cards: group.groupProjects?.length ?? 0,
      projects: group.groupProjects?.length ?? 0,
      users: [],
      isMonthlyBudgetUnlimited: group.isMonthlyBudgetUnlimited || false,
      spent: { currency: 'USD', value: monthlySpend },
      budget: group.isMonthlyBudgetUnlimited ? undefined : { currency: 'USD', value: monthlyBudget },
      groupAdmins,
      createdAt: group.createdAt,
      updatedAt: group.updatedAt,
    };
  });

  return mappedGroups;
};

export function stringAvatar(text: string) {
  let nameSplit = text.toUpperCase().split(' ');
  if (nameSplit.length < 2) nameSplit = text.toUpperCase().split('@');

  const first = nameSplit[0].length > 0 ? nameSplit[0][0] : undefined;
  const second = nameSplit[1].length > 0 ? nameSplit[1][0] : undefined;

  let children = first;
  if (first && second) children += second;

  return { sx: { bgcolor: stringToColor(text) }, children };
}

export const pluralizeWord = (amount: number, message: string) => {
  const text = amount > 1 ? message + 's' : message;
  return `${amount} ${text}`;
};

interface GroupUtilsProps {
  groupType: GroupTypes;
}

export const useCardGroupUtils = ({ groupType }: GroupUtilsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openSidesheet } = useSidesheet();
  const [groupInfo, setGroupInfo] = useState<ProjectGroup | CardGroup | undefined>();
  const [EditCardGroupIsOpen, setEditCardGroupIsOpen] = useState(false);
  const [deleteCardGroupIsOpen, setDeleteCardGroupIsOpen] = useState(false);
  const [paidolUserPictureMap, setPaidolUserPictureMap] = useState<Record<string, string>>({});
  const [isGroupSidesheetOpen, setIsGroupSidesheetOpen] = useState(false);
  const { activeProjects, setActiveProjects } = useCardUtilContext();

  const selectedProjectGroup = useAppSelector(selectSelectedProjectGroup);
  const selectedGroup = useAppSelector(selectSelectedGroup);
  const cardGroupsNextToken = useAppSelector(selectCardGroupsNextToken);
  const projectGroupsNextToken = useAppSelector(selectProjectGroupsNextToken);
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);
  const searchAndFilter = useAppSelector(selectSearchAndFilter);
  const navigate = useNavigate();

  const isProjectGroups = useMemo(() => groupType === GroupTypes.ProjectGroups, [groupType]);
  const selectedGroupType = isProjectGroups ? selectedProjectGroup : selectedGroup;

  const selectGroup = (groupId: string, isProjectGroup: boolean) => {
    isProjectGroup ? dispatch(setSelectedProjectGroupId(groupId)) : dispatch(setSelectedGroupId(groupId));
  };

  const nextToken = isProjectGroups ? projectGroupsNextToken : cardGroupsNextToken;

  const fetchGroups = async (
    shouldFetchMore = false,
    sort?: { field: string; direction: string },
    filter?: Record<string, unknown>
  ) => {
    if (!selectedPaidolId) {
      console.warn('No paidolId provided – skipping fetchGroups');
      return;
    }

    // Fall back to the active view's sort/filter (defaults to name) so refetches preserve order.
    const pageState = searchAndFilter[isProjectGroups ? 'team-project-groups' : 'team-card-groups'];
    const effectiveSort = sort ?? pageState?.sortOption;
    const effectiveFilter =
      filter ??
      (isProjectGroups
        ? generateProjectGroupFilterObject(pageState?.columnFilters ?? {})
        : generateCardGroupFilterObject(pageState?.columnFilters ?? {}));

    const cardGroupArgs = {
      paidolId: selectedPaidolId,
      limit: 100,
      ...(shouldFetchMore && cardGroupsNextToken ? { nextToken: cardGroupsNextToken } : {}),
      ...(effectiveSort ? { sort: effectiveSort } : {}),
      ...(effectiveFilter ? { filter: effectiveFilter } : {}),
    };

    const projectGroupArgs = {
      paidolId: selectedPaidolId,
      ...(shouldFetchMore && projectGroupsNextToken ? { nextToken: projectGroupsNextToken } : {}),
      ...(effectiveSort ? { sort: effectiveSort } : {}),
      ...(effectiveFilter ? { filter: effectiveFilter } : {}),
    };

    const [cardGroupsResult, projectGroupsResult] = await Promise.all([
      dispatch(getGroups(cardGroupArgs)).unwrap(),
      dispatch(getProjectGroups(projectGroupArgs)).unwrap(),
    ]);

    const cardGroups = cardGroupsResult?.v2ListCardGroupsByPaidolId?.items || [];
    const projectGroups = projectGroupsResult?.items || [];

    await afterGetGroups(cardGroups);

    return {
      cardGroupsResult,
      projectGroupsResult,
      cardGroups,
      projectGroups,
    };
  };

  const { enqueueSnackbar } = useSnackbar();
  const { setTrigger } = useUpdateFirstRunState({
    operationName: 'completeFirstRunChecklist',
    firstRunValue: 'createGroup',
  });

  const currentMonth = format(new Date(), 'LLL');

  const onClickGroup = useCallback(
    (groupId: string, groupType: GroupTypes) => {
      if (groupType === GroupTypes.ProjectGroups) {
        navigate(`/team/project-groups/${groupId}/transactions`);
      } else {
        navigate(`/team/card-groups/${groupId}/cards`);
      }
      setIsGroupSidesheetOpen(true);
    },
    [navigate]
  );

  const onCloseGroup = useCallback(() => {
    navigate(`/team/${groupType === GroupTypes.ProjectGroups ? 'project-groups' : 'card-groups'}`, {
      replace: true,
    });
    setIsGroupSidesheetOpen(false);
    dispatch(resetGroupCards());
    const isProject = groupType === GroupTypes.ProjectGroups ? true : false;
    isProject ? dispatch(resetSelectedProjectGroupId()) : dispatch(resetSelectedGroupId());
  }, [dispatch, groupType, navigate]);

  const loadGroup = useCallback(
    async (groupId: string, isProjectGroup: boolean, updateRedux = false) => {
      try {
        let groupResponse: CardGroup | ProjectGroup | undefined;
        if (isProjectGroup) {
          const response = await getProjectGroupData({ groupId });
          // SEE-301: the CDK ProjectGroup is a slim mirror carrying only the fields
          // this selection set requests; Redux and this hook are typed on the full
          // Amplify @model. Same single cast the Amplify-era code did on the
          // equally-slim query result, just relocated to the service boundary.
          groupResponse = response?.projectGroup as ProjectGroup | undefined;
          if (updateRedux && groupResponse) {
            dispatch(updateProjectGroupInState(groupResponse));
          }
          setGroupInfo(groupResponse);
          setActiveProjects(response?.projects);
          setGroupAdmins(response?.groupAdmins);
        } else {
          const response = await getCardGroup({ groupId }, dispatch);
          groupResponse = response;
          setGroupInfo(response);
        }
        selectGroup(groupId, isProjectGroup);
        return groupResponse;
      } catch (error) {
        captureException(error);
        enqueueSnackbar(`Error while loading group details!`, {
          variant: 'error',
        });
      }
    },
    [dispatch]
  );

  const loadGroupCards = useCallback(
    (cardGroup: CardGroup) => {
      if (cardGroup) {
        dispatch(fetchGroupCards({ paidolId: selectedPaidolId, cardGroup }));
      }
    },
    [dispatch, selectedPaidolId]
  );

  const loadMoreCards = useCallback(
    (cardGroup: CardGroup, nextToken: string) => {
      if (cardGroup && nextToken) {
        dispatch(loadMoreGroupCards({ paidolId: selectedPaidolId, cardGroup, nextToken }));
      }
    },
    [dispatch, selectedPaidolId]
  );

  const initialLoad = useCallback(
    (groupId: string, isProjectGroup: boolean) => {
      if (groupId) {
        loadGroup(groupId, isProjectGroup).then((cardGroup) => {
          if (cardGroup?.__typename === 'CardGroup') {
            loadGroupCards(cardGroup);
          }
        });
      }
    },
    [loadGroup, loadGroupCards]
  );

  const getGroupsPictureMap = async (_groups: CardGroup[] | ProjectGroup[] | undefined = []) => {
    const paidolUsers = _groups
      .flatMap(
        (cardGroup) =>
          cardGroup?.paymentCards?.items?.flatMap(
            (paymentCard) => paymentCard?.paidolUsers?.items?.flatMap((rel) => rel?.paidolUser) ?? []
          ) ?? []
      )
      .filter((value) => value !== undefined) as PaidolUser[];

    // Resolved in parallel; a picture that cannot be resolved just keeps the initials avatar.
    const withPictures = paidolUsers.filter((paidolUser) => !!paidolUser.user?.picture);
    const results = await Promise.allSettled(
      withPictures.map((paidolUser) => fileAccessService.getDownloadUrl(paidolUser.user!.picture!))
    );
    const userPictureMap: Record<string, string> = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') userPictureMap[withPictures[index]!.id] = result.value;
    });

    return userPictureMap;
  };

  const afterGetGroups = useCallback(async (_groups: CardGroup[] | ProjectGroup[] | undefined = []) => {
    const map = await getGroupsPictureMap(_groups);

    setPaidolUserPictureMap(map);
  }, []);

  const loadMore = useCallback(() => {
    if (selectedPaidolId && nextToken) {
      fetchGroups(true);
    }
    // fetchGroups is redefined each render (excluded intentionally). A sort/filter change resets
    // nextToken via clearGroups, rebuilding this callback with a fresh closure before it's clickable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaidolId, nextToken]);

  const onSaveGroup = useCallback(
    async ({
      name,
      monthlyBudget,
      isMonthlyBudgetUnlimited,
      cardGroupUsers,
      groupAdmins,
      cards,
      groupType,
      groupProjects,
      glCodeVisibilitySettings,
      projectVisibilitySettings,
      approvalLevels,
      reviewerIds,
    }: EditGroupFormValues) => {
      if (!selectedPaidolId) return;

      let groupId = selectedGroupType?.id;

      const getMonthlyBudget = () =>
        isMonthlyBudgetUnlimited || monthlyBudget === undefined
          ? null
          : {
              value: floatToCurrencyValue(monthlyBudget),
              currency: 'USD',
            };

      try {
        const merchantSpendRuleIdToSave = null;
        const isCreation = !groupId;

        if (GroupTypes.ProjectGroups === groupType) {
          if (groupId) {
            await dispatch(
              updateProject({
                id: groupId,
                name,
                isMonthlyBudgetUnlimited,
                monthlyBudget: getMonthlyBudget(),
                groupProjects,
              })
            ).unwrap();
          } else {
            const createdGroup = await dispatch(
              newProjectGroup({
                // `authPaidolId` is not sent: the CDK resolver derives it from `paidolId`.
                // Accepting it from the caller would let an admin of one tenant mint a
                // group another tenant can read and edit.
                paidolId: selectedPaidolId,
                name,
                isMonthlyBudgetUnlimited,
                monthlyBudget: getMonthlyBudget(),
                groupProjects,
              })
            ).unwrap();

            if (!createdGroup?.id) {
              throw new Error('Failed to create project group: Invalid response received');
            }

            groupId = createdGroup.id;
            setTrigger(true);
          }
        } else {
          if (groupId) {
            await dispatch(
              updateGroup({
                id: groupId,
                name,
                spendGroupId: merchantSpendRuleIdToSave,
                isMonthlyBudgetUnlimited,
                monthlyBudget: getMonthlyBudget(),
                glCodeVisibilitySettings,
                projectVisibilitySettings,
              })
            ).unwrap();
          } else {
            const createdGroup = await dispatch(
              newGroup({
                paidolId: selectedPaidolId,
                authPaidolId: selectedPaidolId,
                spendGroupId: merchantSpendRuleIdToSave,
                name,
                isMonthlyBudgetUnlimited,
                monthlyBudget: getMonthlyBudget(),
                glCodeVisibilitySettings,
                projectVisibilitySettings,
              })
            ).unwrap();

            if (!createdGroup?.id) {
              throw new Error('Failed to create card group: Invalid response received');
            }

            groupId = createdGroup.id;
            setTrigger(true);
          }
        }

        if (!groupId) throw new Error('Failed to create or update the group.');

        if (GroupTypes.ProjectGroups === groupType) {
          await dispatch(
            updateProjectGroupAdmins({
              projectGroupId: groupId,
              groupAdmins: groupAdmins as PaidolUser[],
              paidolId: selectedPaidolId,
              approvalLevels: approvalLevels as Record<string, number[]> | undefined,
              reviewerIds: reviewerIds as string[] | undefined,
            })
          ).unwrap();

          if (groupId) {
            setTimeout(async () => {
              if (groupId) {
                await Promise.all([fetchGroups(), loadGroup(groupId, true, true)]);
              }
            }, 1000);
          }
        } else {
          // Handle card group updates
          // Get cards that are being removed from the group
          const removedCardIds: string[] = [];
          if (!isCreation && selectedGroupType) {
            const currentCardIds =
              (selectedGroupType as CardGroup).paymentCards?.items
                ?.map((card) => card?.paymentCardId)
                .filter(Boolean) || [];
            removedCardIds.push(
              ...(currentCardIds.filter((cardId) => !cards.includes(cardId as string)) as string[])
            );
          }

          // Add the new cards to the group
          await addCardsToGroup(selectedPaidolId, groupId, cards);
          await dispatch(
            updateGroupAdmins({
              cardGroupId: groupId,
              groupAdmins: groupAdmins as PaidolUser[],
              paidolId: selectedPaidolId,
              approvalLevels: approvalLevels as Record<string, number[]> | undefined,
              reviewerIds: reviewerIds as string[] | undefined,
            })
          ).unwrap();

          // Unassign removed cards (if any) before showing success
          if (removedCardIds.length > 0) {
            try {
              await Promise.all(
                removedCardIds.map((cardId) =>
                  dispatch(
                    updateCardGroup({
                      paymentCardId: cardId,
                      cardGroupId: 'NO_GROUP',
                      group: null,
                    })
                  ).unwrap()
                )
              );
            } catch (error) {
              console.error('[onSaveGroup] Error removing cards:', error);
              // Re-throw to be caught by outer catch block
              throw new Error('Failed to remove cards from group');
            }
          }

          // Reset caches and fetch updated data
          await dispatch(resetCardSlice());
          await dispatch(resetProjectGroupsSlice());
          await dispatch(resetGroupsSlice());
          await dispatch(resetAssinableCards());
          await fetchGroups();

          // Refetch cards for the current group to reflect the changes
          if (groupId) {
            initialLoad(groupId, groupType === GroupTypes.ProjectGroups);
          }

          // Only show success and close sheet after everything is complete
          const message = t(isCreation ? 'groupCreated' : 'groupUpdated');
          setEditCardGroupIsOpen(false);

          enqueueSnackbar(message, {
            variant: 'success',
          });
        }
      } catch (error) {
        captureException(error);
        // Surface it: the admin-list write now goes through a role-checked
        // mutation that can reject (wrong role, a non-member id, a 5xx), and
        // the group's other fields may already have saved. Sentry-only left the
        // sheet looking successful.
        enqueueSnackbar(t('groupSaveFailed'), {
          variant: 'error',
        });
      }
    },
    /* Don't include fetchGroups */
    [dispatch, enqueueSnackbar, selectedGroupType?.id, selectedPaidolId, setTrigger, t, initialLoad]
  );

  const handleClickNewCard = (groupId: string) => {
    dispatch(setSelectedGroupId(groupId));
    openSidesheet(SidesheetRoutes.NewCard, {
      formType: 'ISSUE_CARD',
    });
  };

  const handleCloseGroupEdit = () => {
    setEditCardGroupIsOpen(false);
  };

  const handleCloseDeleteGroup = () => {
    setDeleteCardGroupIsOpen(false);
  };

  const handleDeleteCardGroup = async (groupId?: string) => {
    if (!selectedPaidolId || !groupId) return;

    try {
      await dispatch(removeGroup({ paidolId: selectedPaidolId, cardGroupId: groupId })).unwrap();
      setDeleteCardGroupIsOpen(false);
      setIsGroupSidesheetOpen(false);
      navigate('/team/card-groups', { replace: true });
      enqueueSnackbar(t('groupDeleted.groupDeleted'), {
        variant: 'success',
      });
    } catch (error) {
      captureException(error);
      // SEE-301: the delete now goes through a role-checked CDK mutation that can
      // reject, so a non-admin pressing Delete used to get nothing at all -- the
      // dialog closed and the group stayed. The key was already in both locale
      // bundles with copy written for this denial; nothing read it.
      enqueueSnackbar(t('groupDeleted.groupDeleteFailed'), {
        variant: 'error',
      });
    }
  };

  const handleDeleteProjectGroup = async (groupId?: string) => {
    if (!selectedPaidolId || !groupId) return;

    try {
      await dispatch(removeProjectGroup({ projectGroupId: groupId })).unwrap();
      setDeleteCardGroupIsOpen(false);
      setIsGroupSidesheetOpen(false);
      navigate('/team/project-groups', { replace: true });
      enqueueSnackbar(t('groupDeleted.groupDeleted'), {
        variant: 'success',
      });
    } catch (error) {
      captureException(error);
      // SEE-301: the delete now goes through a role-checked CDK mutation that can
      // reject, so a non-admin pressing Delete used to get nothing at all -- the
      // dialog closed and the group stayed. The key was already in both locale
      // bundles with copy written for this denial; nothing read it.
      enqueueSnackbar(t('groupDeleted.groupDeleteFailed'), {
        variant: 'error',
      });
    }
  };

  const onNewGroup = useCallback(
    (groupType: GroupTypes) => {
      groupType === GroupTypes.ProjectGroups
        ? dispatch(setSelectedProjectGroupId(undefined))
        : dispatch(setSelectedGroupId(undefined));
      dispatch(resetGroupCards());
      setEditCardGroupIsOpen(true);
      setActiveProjects([]);
    },
    [dispatch]
  );

  const onEditGroup = useCallback(
    (groupId: string, isProject: boolean) => {
      selectGroup(groupId, isProject);
      setEditCardGroupIsOpen(true);
    },
    [dispatch]
  );

  const onDeleteGroup = useCallback(
    (groupId: string, isProject: boolean) => {
      selectGroup(groupId, isProject);
      setDeleteCardGroupIsOpen(true);
    },
    [dispatch]
  );

  const getCardGroupMenu = (groupId: string, isProject: boolean): MoreVertMenuOptionProps[] => {
    return [
      {
        Icon: <EditIcon color="primary" />,
        label: t('cardGroupActions.editGroup'),
        onClick: () => onEditGroup(groupId, isProject),
      },

      ...(!isProject
        ? [
            {
              Icon: <AddCardIcon color="primary" />,
              label: t('cardGroupActions.createNewCards'),
              onClick: () => handleClickNewCard(groupId),
            },
          ]
        : []),

      {
        Icon: <DeleteOutlineIcon color="primary" />,
        label: t('cardGroupActions.deleteGroup'),
        onClick: () => onDeleteGroup(groupId, isProject),
      },
    ];
  };

  const ProjectGroupTableHeader: Array<Omit<ResponsiveTableHeadColumn, 'label'> & { label: string }> = [
    {
      id: 'groupName',
      label: t('group'),
    },
    {
      id: 'numCards',
      label: '# of Projects',
    },
    {
      id: 'members',
      label: t('groupAdmins'),
    },
    {
      id: 'spent',
      label: t('spentIn', { month: currentMonth }),
      align: 'right',
    },
    {
      id: 'groupBudget',
      label: t('monthSpendGuidance', { month: currentMonth }),
    },
    {
      id: 'groupEdit',
      label: '',
    },
  ];

  const CardGroupTableHeader: Array<Omit<ResponsiveTableHeadColumn, 'label'> & { label: string }> = [
    {
      id: 'groupName',
      label: t('group'),
    },
    {
      id: 'numCards',
      label: t('numCards'),
    },
    {
      id: 'members',
      label: t('members'),
    },
    {
      id: 'spent',
      label: t('spentIn', { month: currentMonth }),
      align: 'right',
    },
    {
      id: 'groupBudget',
      label: t('monthSpendGuidance', { month: currentMonth }),
    },
    {
      id: 'groupEdit',
      label: '',
    },
  ];

  return {
    getCardGroupMenu,
    handleClickNewCard,
    EditCardGroupIsOpen,
    handleCloseGroupEdit,
    onSaveGroup,
    loadMore,
    afterGetGroups,
    onNewGroup,
    paidolUserPictureMap,
    loadGroup,
    initialLoad,
    loadGroupCards,
    loadMoreCards,
    groupInfo,
    handleDeleteCardGroup,
    handleDeleteProjectGroup,
    deleteCardGroupIsOpen,
    handleCloseDeleteGroup,
    onClickGroup,
    onCloseGroup,
    isGroupSidesheetOpen,
    ProjectGroupTableHeader,
    CardGroupTableHeader,
    fetchGroups,
    activeProjects,
  };
};
